import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BoardResponse,
  BoardColumn,
  TaskCard,
  ExternalCreateTaskInput,
} from '../services/api/board';
import { createExternalTask } from '../services/api/board';

export interface CreateExternalTaskVariables {
  projectId: string;
  input: ExternalCreateTaskInput;
}

interface MutationContext {
  previous: BoardResponse | undefined;
  tempId: number;
}

/**
 * Phase 25 Plan 25-07 EXT-01 + D-23 — external task create with optimistic
 * prepend into the first (lowest-ordering) column of the external board
 * cache. The cache key is locked to ['board', projectId, 'external']
 * (Phase 24 D-12 / queryKey shape from 24-CONTEXT.md). External mutations
 * never touch the internal cache slot — Pitfall 6 from Phase 24.
 *
 * Phase 51 UAT #3 — there is no client opt-in: every external create goes to
 * Triage. The optimistic card targets the isTriage column (taskStateId=null)
 * when one is present on the board, else falls back to the first real column
 * (non-intake projects have no Triage column). The server forces the actual
 * column assignment; optimistic state is cosmetic (T-51-72).
 */
export function useCreateExternalTask() {
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<typeof createExternalTask>>,
    Error,
    CreateExternalTaskVariables,
    MutationContext
  >({
    mutationFn: ({ projectId, input }) => createExternalTask(projectId, input),
    onMutate: async ({ projectId, input }) => {
      const key = ['board', projectId, 'external'] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardResponse>(key);
      const tempId = -Date.now(); // negative — server ids are positive
      if (previous) {
        const next = applyOptimisticExternalCreate(
          previous,
          input,
          tempId,
          Number(projectId) || 0,
        );
        queryClient.setQueryData<BoardResponse>(key, next);
      }
      return { previous, tempId };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(['board', vars.projectId, 'external'], ctx.previous);
      }
    },
    onSuccess: (server, vars, ctx) => {
      if (!ctx) return;
      const key = ['board', vars.projectId, 'external'] as const;
      const current = queryClient.getQueryData<BoardResponse>(key);
      if (!current) return;
      // Reconcile temp id → server id IN THE SAME COLUMN we placed it.
      const reconciled: BoardResponse = {
        columns: current.columns.map((col) => ({
          ...col,
          tasks: col.tasks.map((t) =>
            t.id === ctx.tempId ? serverTaskToCardRow(server) : t,
          ),
        })),
      };
      queryClient.setQueryData<BoardResponse>(key, reconciled);
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['board', vars.projectId, 'external'],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Pure helpers — exported for unit testing.
// ---------------------------------------------------------------------------

export function applyOptimisticExternalCreate(
  board: BoardResponse,
  input: ExternalCreateTaskInput,
  tempId: number,
  projectId: number,
): BoardResponse {
  if (board.columns.length === 0) return board; // no states → nothing to prepend into

  const optimistic: TaskCard = {
    id: tempId,
    name: input.name,
    code: null,
    description: input.description ?? null,
    isExternal: true,
    createdAt: new Date().toISOString(),
    projectId,
    milestoneId: null, // Phase 30 — externally created tasks start unassigned
    budget: null,
    hardLimit: false,
    plannedHours: null,
    reportedHours: 0,
    canFlipVisibility: false, // externals can never flip visibility
    taskStateId: null, // set below based on target column
  };

  // Phase 51 UAT #3 — every external create goes to Triage. Target the
  // isTriage column (taskStateId=null) when the board already exposes one.
  const triageColIdx = board.columns.findIndex((c) => c.isTriage);
  if (triageColIdx >= 0) {
    const triageCard: TaskCard = { ...optimistic, taskStateId: null };
    const nextCols: BoardColumn[] = board.columns.map((col, idx) =>
      idx === triageColIdx ? { ...col, tasks: [triageCard, ...col.tasks] } : col,
    );
    return { columns: nextCols };
  }

  // Fallback: no synthetic Triage column yet (e.g. this is the first triage
  // task — the column only appears once ≥1 triage task exists — or a
  // non-intake project). Prepend into the first non-triage column; the board
  // refetch on settle reconciles to the server's actual placement.
  // Phase 24 D-01: columns are sorted by ordering ascending, so columns[0] is
  // canonical UNLESS it is the synthetic triage column; in that case use the
  // first non-triage column.
  const firstRealColIdx = board.columns.findIndex((c) => !c.isTriage);
  if (firstRealColIdx < 0) return board; // no real columns

  const firstCol = board.columns[firstRealColIdx]!;
  const defaultCard: TaskCard = { ...optimistic, taskStateId: firstCol.taskStateId };
  const nextCols: BoardColumn[] = board.columns.map((col, idx) =>
    idx === firstRealColIdx ? { ...col, tasks: [defaultCard, ...col.tasks] } : col,
  );
  return { columns: nextCols };
}

export function serverTaskToCardRow(
  server: Awaited<ReturnType<typeof createExternalTask>>,
): TaskCard {
  return {
    id: server.id,
    name: server.name,
    code: null,
    description: server.description,
    // Phase 51 INTAKE-01 (CF-2) — taskStateId may be null for triage tasks.
    taskStateId: server.taskStateId,
    isExternal: server.isExternal,
    // taskMutationResponseSchema does not enumerate createdAt for the
    // external POST shape; the next board refetch (onSettled invalidate)
    // replaces this temporary timestamp with the server-derived value.
    createdAt: new Date().toISOString(),
    projectId: server.projectId,
    milestoneId: null, // Phase 30 — external POST response doesn't include milestoneId; refetch will populate
    budget: null,
    hardLimit: false,
    plannedHours: null,
    reportedHours: 0,
    canFlipVisibility: false,
  };
}
